import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import {
  TrendingUp, BarChart3, CandlestickChart, Shield, BookOpen, ArrowRight,
  Zap, Target, LineChart, Layers
} from 'lucide-react'
import AnimatedSection from '../components/AnimatedSection'
import SectionHeading from '../components/SectionHeading'

const features = [
  {
    icon: CandlestickChart,
    title: 'Candlestick Patterns',
    description: 'Master the art of reading candlestick formations to predict market movements with precision.',
  },
  {
    icon: TrendingUp,
    title: 'Technical Analysis',
    description: 'Learn proven technical indicators and chart patterns to identify high-probability trade setups.',
  },
  {
    icon: BarChart3,
    title: 'Market Trends',
    description: 'Understand bull and bear markets, trend reversals, and momentum shifts for smarter entries.',
  },
  {
    icon: Shield,
    title: 'Risk Management',
    description: 'Protect your capital with disciplined position sizing, stop-loss strategies, and portfolio rules.',
  },
  {
    icon: Zap,
    title: 'Crypto Markets',
    description: 'Navigate the volatile world of cryptocurrencies with structured analysis and trading frameworks.',
  },
  {
    icon: Target,
    title: 'Trading Psychology',
    description: 'Develop emotional discipline, overcome FOMO, and build a strategic mindset for long-term success.',
  },
]

const stats = [
  { value: '50+', label: 'Educational Videos' },
  { value: '10K+', label: 'Community Members' },
  { value: '24/7', label: 'Market Insights' },
  { value: '100%', label: 'Free Education' },
]

const topics = [
  { icon: LineChart, title: 'Support & Resistance', desc: 'Identify key price levels that drive market decisions.' },
  { icon: Layers, title: 'Chart Patterns', desc: 'Recognize head & shoulders, triangles, flags, and more.' },
  { icon: BookOpen, title: 'Fundamental Analysis', desc: 'Evaluate company financials and economic indicators.' },
]

export default function Home() {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[92vh] flex items-center justify-center overflow-hidden">
        {/* Background gradients */}
        <div className="absolute inset-0 bg-gradient-to-br from-brand-500/5 via-transparent to-brand-600/5 dark:from-brand-500/10 dark:to-brand-900/20" />
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-brand-500/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-20 right-1/4 w-80 h-80 bg-brand-600/10 rounded-full blur-[100px]" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mb-8"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass text-xs font-medium text-brand-600 dark:text-brand-400 mb-6">
              <span className="w-2 h-2 rounded-full bg-brand-500 animate-pulse" />
              Free Trading Education for Everyone
            </div>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tight text-[var(--text-primary)] mb-6 text-balance"
          >
            Master the{' '}
            <span className="gradient-text">Stock Market</span>
            <br className="hidden sm:block" />{' '}
            & <span className="gradient-text">Crypto</span> Trading
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="text-lg md:text-xl text-[var(--text-secondary)] max-w-2xl mx-auto mb-10 leading-relaxed text-balance"
          >
            FiveTrader is your gateway to expert trading education. Learn technical analysis,
            candlestick patterns, and investment strategies to build long-term wealth with discipline.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Link
              to="/learn"
              className="group inline-flex items-center gap-2 px-8 py-4 bg-brand-600 hover:bg-brand-700 text-white font-semibold rounded-2xl transition-all duration-300 shadow-lg shadow-brand-500/25 hover:shadow-brand-500/40 hover:-translate-y-0.5"
            >
              Start Learning
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <a
              href="https://www.youtube.com/@fivetrader"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 glass text-[var(--text-primary)] font-semibold rounded-2xl hover:bg-[var(--bg-secondary)] transition-all duration-300"
            >
              Watch on YouTube
            </a>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto"
          >
            {stats.map((stat, i) => (
              <div key={i} className="glass rounded-2xl p-5 text-center card-hover">
                <div className="text-2xl md:text-3xl font-bold gradient-text">{stat.value}</div>
                <div className="text-xs md:text-sm text-[var(--text-secondary)] mt-1">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 md:py-32 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-brand-500/[0.02] to-transparent" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <SectionHeading
            subtitle="What You Will Learn"
            title="Comprehensive Trading Education"
            description="From candlestick patterns to crypto analysis, we cover everything you need to become a confident, disciplined trader."
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => (
              <AnimatedSection key={i} delay={i * 0.1}>
                <div className="group glass rounded-3xl p-8 h-full card-hover glow-border">
                  <div className="w-12 h-12 rounded-2xl bg-brand-500/10 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
                    <feature.icon size={24} className="text-brand-600 dark:text-brand-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">{feature.title}</h3>
                  <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{feature.description}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Topics Preview */}
      <section className="py-24 md:py-32 bg-[var(--bg-secondary)] relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-brand-500/5 rounded-full blur-[150px]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection>
              <span className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold tracking-wide uppercase bg-brand-500/10 text-brand-600 dark:text-brand-400 mb-4">
                Core Topics
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-[var(--text-primary)] mb-6 text-balance">
                Build Your Trading <span className="gradient-text">Foundation</span>
              </h2>
              <p className="text-base md:text-lg text-[var(--text-secondary)] leading-relaxed mb-8 text-balance">
                Whether you are just starting out or refining your edge, our structured curriculum
                takes you from basics to advanced strategies with real-world examples and actionable insights.
              </p>
              <Link
                to="/learn"
                className="group inline-flex items-center gap-2 px-6 py-3 bg-brand-600 hover:bg-brand-700 text-white font-semibold rounded-xl transition-all duration-300 shadow-lg shadow-brand-500/25"
              >
                Explore All Topics
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </AnimatedSection>

            <div className="space-y-4">
              {topics.map((topic, i) => (
                <AnimatedSection key={i} delay={i * 0.15}>
                  <div className="glass rounded-2xl p-6 flex items-start gap-5 card-hover">
                    <div className="w-11 h-11 rounded-xl bg-brand-500/10 flex items-center justify-center shrink-0">
                      <topic.icon size={20} className="text-brand-600 dark:text-brand-400" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-[var(--text-primary)] mb-1">{topic.title}</h4>
                      <p className="text-sm text-[var(--text-secondary)]">{topic.desc}</p>
                    </div>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-600/10 via-transparent to-brand-800/10" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <AnimatedSection>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-[var(--text-primary)] mb-6 text-balance">
              Ready to <span className="gradient-text">Level Up</span> Your Trading?
            </h2>
            <p className="text-lg text-[var(--text-secondary)] mb-10 max-w-2xl mx-auto text-balance">
              Join thousands of learners on our YouTube channel. Subscribe for weekly market analysis,
              trading strategies, and educational content delivered straight to you.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://www.youtube.com/@fivetrader"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-2 px-8 py-4 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-2xl transition-all duration-300 shadow-lg shadow-red-500/25 hover:shadow-red-500/40 hover:-translate-y-0.5"
              >
                Subscribe on YouTube
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="https://t.me/+lJRKb2LzvTwxMTZl"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 glass text-[var(--text-primary)] font-semibold rounded-2xl hover:bg-[var(--bg-secondary)] transition-all duration-300"
              >
                Join Telegram
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  )
}
